import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="postal-lookup"
export default class extends Controller {
  static targets = ["input", "city", "province", "status", "lenderInfo"]
  static values = {
    url: { type: String, default: "/api/postal_lookups" }
  }

  connect() {
    this.timeout = null
    this.lookupData = null  // Store lookup data until postal code is complete
  }

  lookup() {
    clearTimeout(this.timeout)

    const input = this.inputTarget.value
    const fsa = this.extractFSA(input)
    const isComplete = this.isCompletePostalCode(input)

    if (fsa.length < 3) {
      this.clearStatus()
      this.lookupData = null
      return
    }

    // Show loading only if postal code is complete
    if (isComplete) {
      this.showLoading()
    }

    // Debounce API call
    this.timeout = setTimeout(() => {
      this.performLookup(fsa, isComplete)
    }, 500)
  }

  async performLookup(fsa, showResults) {
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.getCSRFToken()
        },
        body: JSON.stringify({ fsa: fsa })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Request failed" }))
        this.handleError(errorData.error || "Could not verify postal code")
        return
      }

      const data = await response.json()
      this.lookupData = data  // Store for later

      // Only show results if postal code is complete
      if (showResults) {
        this.handleSuccess(data)
      }
    } catch (error) {
      if (showResults) {
        this.handleError("Network error. Please try again.")
      }
      console.error("Postal lookup error:", error)
    }
  }

  handleSuccess(data) {
    const { province, cities } = data

    // Set province
    if (this.hasProvinceTarget) {
      this.provinceTarget.value = province
    }

    // Handle cities
    if (!this.hasCityTarget) return

    // Enable city field now that postal code is complete
    this.cityTarget.readOnly = false
    this.cityTarget.placeholder = "Calgary"
    this.cityTarget.classList.remove("bg-base-300", "cursor-not-allowed")

    if (cities.length === 1) {
      // Single city - auto-fill
      this.cityTarget.value = cities[0]
      this.showSuccess(`✓ ${cities[0]}, ${province}`)
    } else if (cities.length > 1) {
      // Multiple cities - show dropdown
      this.showCityDropdown(cities, province)
    }

    // Show lender availability info
    this.showLenderInfo(province)
  }

  isCompletePostalCode(input) {
    // Check if postal code has 6 characters (ignoring spaces/dashes)
    const cleaned = input.replace(/[\s-]/g, '')
    return cleaned.length >= 6
  }

  handleError(message) {
    this.showError(message)

    if (this.hasCityTarget) {
      this.cityTarget.value = ""
      this.cityTarget.readOnly = true
      this.cityTarget.placeholder = "Enter postal code first"
      this.cityTarget.classList.add("bg-base-300", "cursor-not-allowed")
    }

    if (this.hasProvinceTarget) {
      this.provinceTarget.value = ""
    }
  }

  showCityDropdown(cities, province) {
    // Convert text input to select dropdown
    const currentValue = this.cityTarget.value
    const select = document.createElement("select")
    select.name = this.cityTarget.name
    select.id = this.cityTarget.id
    select.className = this.cityTarget.className
    select.required = this.cityTarget.required
    select.dataset.postalLookupTarget = "city"

    // Add placeholder option
    const placeholder = document.createElement("option")
    placeholder.value = ""
    placeholder.textContent = "Select your city..."
    select.appendChild(placeholder)

    // Add city options
    cities.forEach(city => {
      const option = document.createElement("option")
      option.value = city
      option.textContent = city
      if (city === currentValue) {
        option.selected = true
      }
      select.appendChild(option)
    })

    // Replace input with select
    this.cityTarget.parentNode.replaceChild(select, this.cityTarget)

    this.showSuccess(`✓ Found ${cities.length} communities in ${province}`)
  }

  showLenderInfo(province) {
    if (!this.hasLenderInfoTarget) return

    const lenderCount = this.countLendersForProvince(province)
    const lenderText = lenderCount === 1
      ? "1 lender serves this area"
      : `There are ${lenderCount} lenders that lend in this province!`

    this.lenderInfoTarget.textContent = lenderText
    this.lenderInfoTarget.classList.remove("hidden")
  }

  countLendersForProvince(province) {
    let count = 1 // CHIP always available
    if (["ON", "BC", "AB", "QC"].includes(province)) count++  // Equitable
    if (["ON", "BC", "AB"].includes(province)) count++        // Bloom
    return count
  }

  showLoading() {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = "Looking up postal code..."
      this.statusTarget.className = "text-sm text-base-content/60 mt-1"
    }
  }

  showSuccess(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      this.statusTarget.className = "text-sm text-success mt-1"
    }
  }

  showError(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      this.statusTarget.className = "text-sm text-error mt-1"
    }
  }

  clearStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = ""
    }
    if (this.hasLenderInfoTarget) {
      this.lenderInfoTarget.classList.add("hidden")
    }
  }

  extractFSA(input) {
    return input.replace(/[\s-]/g, "").toUpperCase().slice(0, 3)
  }

  getCSRFToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  disconnect() {
    clearTimeout(this.timeout)
  }
}
